import { useTranslation } from "react-i18next";
import { Label, Input } from "@tellescope/design-system";
import { StringSelect } from "@tellescope/ui";
import type { FileUploadBlock } from "../../../../../shared/schema";
import { LayoutSection } from "../shared/LayoutSection";
import { VisibilitySection } from "../shared/VisibilitySection";
import type { BlockTypeEditorProps } from "../types";

type FileUploadBlockEditorProps = BlockTypeEditorProps<FileUploadBlock>;

export function FileUploadBlockEditor({
  block,
  updateProps,
  updateLayout,
  updateVisibility,
  layoutOpen,
  setLayoutOpen,
  visibilityOpen,
  setVisibilityOpen,
}: FileUploadBlockEditorProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <StringSelect
        label={t("Variant")}
        value={block.props.variant ?? "button"}
        onValueChange={(value) => updateProps({ variant: value })}
        options={[
          { value: "button", label: t("Button") },
          { value: "icon", label: t("Icon") },
        ]}
      />
      {/* Button Style - only visible when variant is "button" */}
      {block.props.variant !== "icon" && (
        <StringSelect
          label={t("Button Style")}
          value={block.props.buttonVariant ?? "default"}
          onValueChange={(value) => updateProps({ buttonVariant: value })}
          options={[
            { value: "default", label: t("Default") },
            { value: "secondary", label: t("Secondary") },
            { value: "outline", label: t("Outline") },
            { value: "ghost", label: t("Ghost") },
          ]}
        />
      )}
      <div className="space-y-2">
        <Label>{t("Text")}</Label>
        <Input
          value={block.props.text ?? "Upload File"}
          onChange={(e) => updateProps({ text: e.target.value || "Upload File" })}
          placeholder={t("Upload File")}
        />
        <p className="text-xs text-muted-foreground">
          {block.props.variant === "icon" ? t("Shown as tooltip") : t("Button label")}
        </p>
      </div>
      <div className="space-y-2">
        <Label>{t("Accepted File Types")}</Label>
        <Input
          value={block.props.acceptedFileTypes ?? ""}
          onChange={(e) => updateProps({ acceptedFileTypes: e.target.value || undefined })}
          placeholder={t("e.g., image/*,.pdf,.doc")}
        />
        <p className="text-xs text-muted-foreground">{t("Leave empty to accept all file types")}</p>
      </div>
      <div className="space-y-2">
        <Label>{t("Max File Size (MB)")}</Label>
        <Input
          type="number"
          min={1}
          max={100}
          value={block.props.maxFileSizeMB ?? ""}
          onChange={(e) => updateProps({ maxFileSizeMB: e.target.value ? Number(e.target.value) : undefined })}
          placeholder={t("e.g., 10")}
        />
        <p className="text-xs text-muted-foreground">{t("Leave empty for no size limit (1-100 MB)")}</p>
      </div>
      <LayoutSection layout={block.layout} onChange={updateLayout} open={layoutOpen} onOpenChange={setLayoutOpen} />
      <VisibilitySection
        visibility={block.visibility}
        onChange={updateVisibility}
        open={visibilityOpen}
        onOpenChange={setVisibilityOpen}
      />
    </div>
  );
}
